/**
 * Yerel ayar — VERİ, kod değil.
 *
 * Bu paket kurulmadan önce sistem Türkiye'yi koda gömmüştü: tarihler koşulsuz
 * GG/AA okunuyor, tanınmayan ay adı sessizce Ocak oluyor, `1.234` her zaman
 * 1234'e çevriliyordu. Bunlar hata vermiyor, YANLIŞ VERİ yazıyordu.
 *
 * Buradaki kural tek cümle: ayrıştırıcı hiçbir zaman tahmin etmez. Bir değeri
 * ya kanıtla okur, ya da "emin değilim" der ve kullanıcı onayına bırakır.
 * Muhasebede yanlış rakam, okunamayan belgeden kat kat pahalıdır.
 *
 * Yeni bir ülke eklemek = LOCALES'e bir satır. Başka hiçbir yere dokunulmaz.
 */

/** Tarihteki üç sayının hangi sırada olduğu. */
export type DateOrder = 'dmy' | 'mdy' | 'ymd';

export interface Locale {
  /** BCP-47 etiketi (en-US, de-DE, tr-TR ...). */
  readonly tag: string;
  readonly dateOrder: DateOrder;
  /** Ondalık ayracı. */
  readonly decimal: '.' | ',';
  /** Binlik ayracı. Boş dize = ayraç yok. */
  readonly group: '.' | ',' | ' ' | '';
  /** ISO 4217. Varsayılan YOK — kurulumda seçtirilir (aşağıdaki nota bak). */
  readonly currency: string;
}

/**
 * Desteklenen yerel ayarlar.
 *
 * Liste bilinçli olarak kısa: her satır test edilmiş bir davranıştır, süs
 * değildir. Bilinmeyen bir etiket geldiğinde uydurmak yerine FALLBACK'e
 * düşeriz ve belirsiz değerler onaya çıkar.
 */
export const LOCALES: Readonly<Record<string, Locale>> = {
  'en-US': { tag: 'en-US', dateOrder: 'mdy', decimal: '.', group: ',', currency: 'USD' },
  'en-GB': { tag: 'en-GB', dateOrder: 'dmy', decimal: '.', group: ',', currency: 'GBP' },
  'en-CA': { tag: 'en-CA', dateOrder: 'ymd', decimal: '.', group: ',', currency: 'CAD' },
  'en-AU': { tag: 'en-AU', dateOrder: 'dmy', decimal: '.', group: ',', currency: 'AUD' },
  'de-DE': { tag: 'de-DE', dateOrder: 'dmy', decimal: ',', group: '.', currency: 'EUR' },
  'fr-FR': { tag: 'fr-FR', dateOrder: 'dmy', decimal: ',', group: ' ', currency: 'EUR' },
  'es-ES': { tag: 'es-ES', dateOrder: 'dmy', decimal: ',', group: '.', currency: 'EUR' },
  'it-IT': { tag: 'it-IT', dateOrder: 'dmy', decimal: ',', group: '.', currency: 'EUR' },
  'nl-NL': { tag: 'nl-NL', dateOrder: 'dmy', decimal: ',', group: '.', currency: 'EUR' },
  'pt-BR': { tag: 'pt-BR', dateOrder: 'dmy', decimal: ',', group: '.', currency: 'BRL' },
  'tr-TR': { tag: 'tr-TR', dateOrder: 'dmy', decimal: ',', group: '.', currency: 'TRY' },
  'ar-AE': { tag: 'ar-AE', dateOrder: 'dmy', decimal: '.', group: ',', currency: 'AED' },
  'ja-JP': { tag: 'ja-JP', dateOrder: 'ymd', decimal: '.', group: ',', currency: 'JPY' },
  'zh-CN': { tag: 'zh-CN', dateOrder: 'ymd', decimal: '.', group: ',', currency: 'CNY' },
};

/**
 * Tanınmayan yerel ayar için geri düşüş.
 *
 * ISO tarih sırası (YYYY-AA-GG) ve nokta ondalık seçildi: ikisi de makine
 * standardı ve ikisi de belirsizlik ÜRETMEZ. Bilmediğimiz bir ülke için
 * en güvenli davranış, belirsiz her değeri onaya düşürmektir.
 */
export const FALLBACK: Locale = {
  tag: 'und', dateOrder: 'ymd', decimal: '.', group: ',', currency: '',
};

/**
 * Etiketten yerel ayar bul.
 *
 * Tam eşleşme yoksa dil koduna göre en yakınına düşer (`de-AT` → `de-DE`).
 * Hiçbiri tutmazsa FALLBACK. Uydurma yapılmaz.
 */
export function resolveLocale(tag: string | null | undefined): Locale {
  if (!tag) return FALLBACK;
  const want = String(tag).replace('_', '-');
  const exact = LOCALES[want];
  if (exact) return exact;
  const lang = want.split('-')[0].toLowerCase();
  for (const key of Object.keys(LOCALES)) {
    if (key.split('-')[0].toLowerCase() === lang) return LOCALES[key];
  }
  return FALLBACK;
}

/**
 * Tarayıcının dilinden yerel ayar. Sunucu tarafında çağrılırsa FALLBACK.
 *
 * Not: para birimi buradan TÜRETİLMEZ. Tarayıcı dili kullanıcının parasını
 * bilmez — Almanya'da yaşayan biri USD ile defter tutuyor olabilir. Para
 * birimi kurulumda AÇIKÇA seçtirilir; yanlış varsayılan her tutarı sessizce
 * bozar, ve bu tam olarak `auth.js`'in bugün yaptığı hatadır.
 */
export function localeFromBrowser(): Locale {
  const nav = (globalThis as { navigator?: { language?: string } }).navigator;
  return resolveLocale(nav?.language ?? null);
}
